import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  Bug, Droplets, Factory, Recycle, TreePine,
  CheckCircle2, Phone, ArrowRight, Star, Shield, Clock, Award, Users, ChevronLeft, ChevronRight
} from 'lucide-react';
import { HeroSection } from '../components/ui/HeroSection';
import PageSEO from '../components/PageSEO';

const homeSEO = {
  title: 'Lidav Enterprises Limited | Fumigation, Pest Control & Waste Management Kenya',
  description: "Kenya's #1 NEMA-licensed facilities management company. Professional fumigation, pest control, sanitary disposal, hazardous waste management & landscaping in Nairobi. Free site assessment. Call 0722 148 999.",
  canonical: '/',
  schema: {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Lidav Enterprises Limited – Home',
    url: 'https://lidaventerprises.com',
    description: "Kenya's #1 NEMA-licensed facilities management company. Professional fumigation, pest control, sanitary disposal, hazardous waste management & landscaping.",
    breadcrumb: { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://lidaventerprises.com' }] },
    mainEntity: {
      '@type': 'ItemList',
      name: 'Our Services',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Fumigation & Pest Control', url: 'https://lidaventerprises.com/services' },
        { '@type': 'ListItem', position: 2, name: 'Sanitary Disposal Services', url: 'https://lidaventerprises.com/services' },
        { '@type': 'ListItem', position: 3, name: 'Hazards & Industrial Waste Management', url: 'https://lidaventerprises.com/services' },
        { '@type': 'ListItem', position: 4, name: 'General Waste Management', url: 'https://lidaventerprises.com/services' },
        { '@type': 'ListItem', position: 5, name: 'Landscaping Services', url: 'https://lidaventerprises.com/services' },
      ],
    },
  },
};

/* ─── Animation helpers ─── */
const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 36, scale: 0.98 },
  whileInView: { opacity: 1, y: 0,  scale: 1    },
  viewport:    { once: true, margin: '-80px'     },
  transition:  { duration: 0.6, ease: 'easeOut', delay },
});
const staggerContainer = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};
const staggerItem = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  show:   { opacity: 1, y: 0,  scale: 1, transition: { duration: 0.5, ease: 'easeOut' } },
};

/* ─── Animated counter ─── */
function Counter({ end, suffix = '', prefix = '' }) {
  const ref    = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const dur = 2000, fps = 60;
    const step = end / (dur / (1000 / fps));
    let cur = 0;
    const t = setInterval(() => {
      cur += step;
      if (cur >= end) { setCount(end); clearInterval(t); }
      else setCount(Math.floor(cur));
    }, 1000 / fps);
    return () => clearInterval(t);
  }, [inView, end]);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

/* ─── Data ─── */
const services = [
  { icon: Bug,             gradient: 'from-red-600 to-red-800',      bg: 'bg-red-50',    accent: '#E30613', title: 'Fumigation & Pest Control',  desc: 'Complete elimination of bedbugs, cockroaches, termites, rodents, mosquitoes and all pest infestations using NEMA-approved chemicals.' },
  { icon: Droplets,        gradient: 'from-blue-600 to-blue-800',    bg: 'bg-blue-50',   accent: '#02AEF2', title: 'Sanitary Disposal Services',  desc: 'Hygienic, discreet collection and disposal of sanitary waste from offices, hospitals and public facilities.' },
  { icon: Factory,         gradient: 'from-orange-600 to-amber-700', bg: 'bg-orange-50', accent: '#f97316', title: 'Hazards & Industrial Waste',   desc: 'Safe handling, transport and disposal of hazardous industrial waste in compliance with NEMA and KEBS regulations.' },
  { icon: Recycle,         gradient: 'from-red-500 to-red-700',      bg: 'bg-red-50',    accent: '#E30613', title: 'General Waste Management',    desc: 'Comprehensive waste collection, sorting, recycling and responsible disposal for homes and businesses.' },
  { icon: TreePine,        gradient: 'from-red-400 to-red-600',      bg: 'bg-red-50',    accent: '#E30613', title: 'Landscaping Services',         desc: 'Garden design, lawn maintenance, tree planting and beautiful green space management for your property.' },
];

const whyChoose = [
  { icon: Shield,       text: 'NEMA Licensed & Certified Professionals' },
  { icon: Clock,        text: '24/7 Emergency Response Available' },
  { icon: Award,        text: '15+ Years of Industry Excellence' },
  { icon: CheckCircle2, text: 'Eco-Friendly & Safe Chemical Solutions' },
  { icon: Users,        text: 'Trained, Uniformed & Vetted Teams' },
  { icon: Star,         text: '100% Client Satisfaction Guarantee' },
];

const testimonials = [
  { name: 'James Mwangi',       role: 'Facilities Manager, Nairobi Serena Hotel',  rating: 5, text: 'Lidav Enterprises has been our trusted fumigation partner for over 5 years. Their team is professional, thorough and always on time. Highly recommended!' },
  { name: 'Dr. Amina Odhiambo', role: 'Administrator, Nairobi West Hospital',      rating: 5, text: "For a healthcare facility, hygiene is paramount. Lidav's comprehensive cleaning and waste management services have been outstanding. Zero complaints from our inspectors." },
  { name: 'Peter Kariuki',      role: 'Property Manager, Westlands Business Park', rating: 5, text: 'We engaged Lidav for landscaping and general cleaning of our complex. The results are breathtaking — our tenants have never been happier. Great value for money!' },
  { name: 'Sarah Achieng',      role: 'HR Director, Kenya Commercial Bank',        rating: 5, text: 'The office catering services from Lidav are exceptional. Our staff love the quality of food and service. Their team blends in seamlessly with our corporate culture.' },
];

/* ─── Service card ─── */
function ServiceCard({ service, index }) {
  const { icon: Icon, gradient, bg, accent, title, desc } = service;
  const num = String(index + 1).padStart(2, '0');
  return (
    <Link
      to="/services"
      className="group relative rounded-2xl p-6 overflow-hidden cursor-pointer transition-all duration-300 hover:-translate-y-2 no-underline"
      style={{
        background:           'rgba(255,255,255,0.88)',
        backdropFilter:       'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border:               '1px solid rgba(255,255,255,0.65)',
        boxShadow:            '0 4px 24px rgba(2,174,242,0.07)',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 20px 56px rgba(2,174,242,0.16)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 24px rgba(2,174,242,0.07)'; }}
    >
      <motion.div variants={staggerItem}>
        {/* Gradient top bar */}
        <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${gradient} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-400 z-10`} />

        {/* Number watermark */}
        <span className="absolute top-3 right-4 text-[3.5rem] font-black text-gray-50 select-none leading-none group-hover:text-brand-blue/4 transition-colors duration-300 font-poppins">
          {num}
        </span>

        {/* Icon */}
        <div className={`relative z-10 w-12 h-12 ${bg} rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}>
          <div className={`w-8 h-8 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center shadow-sm`}>
            <Icon size={16} className="text-white" />
          </div>
        </div>

        {/* Content */}
        <h3 className="relative z-10 font-bold font-poppins text-gray-900 text-[15px] mb-2 leading-snug group-hover:text-brand-blue transition-colors duration-200">
          {title}
        </h3>
        <p className="relative z-10 text-gray-500 text-[13px] font-inter leading-relaxed line-clamp-3">
          {desc}
        </p>

        {/* Explore arrow */}
        <div
          className="relative z-10 mt-4 flex items-center gap-1.5 text-[11px] font-semibold font-inter opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
          style={{ color: accent }}
        >
          Explore Service <ArrowRight size={11} />
        </div>
      </motion.div>
    </Link>
  );
}

/* ════════════════════════════════════════════ */
export default function Home() {
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTestimonialIdx(v => (v + 1) % testimonials.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <main>
      <PageSEO {...homeSEO} />
      {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
      <HeroSection
        logo={{ url: '/images/logo.jpg', alt: 'Lidav Enterprises Limited', text: 'LIDAV ENTERPRISES' }}
        slogan="Limited"
        title={
          <>
            Professional Solutions for <br />
            <span style={{ color: '#E30613' }}>Cleaner, Safer</span>
            {' '}&amp; Healthier Environment
          </>
        }
        subtitle="Kenya's trusted facilities management and environmental services company — protecting health, environment and spaces for 500+ clients across Nairobi and beyond."
        callToAction={{ text: 'Get a Free Quote', href: '/contact' }}
        secondaryAction={{ text: '0722 148 999', href: 'tel:0722148999' }}
        backgroundImage="/images/3.jpg"
        contactInfo={{
          website: 'lidaventerpriseltd',
          phone:   '0722 148 999',
          address: 'Utalii House, Uhuru Highway, Nairobi',
        }}
      />

      {/* ══════════════════════════════════════════
          SERVICES
      ══════════════════════════════════════════ */}
      <section className="py-24 bg-[#f0f7ff] relative overflow-hidden">
        {/* Decorative panel */}
        <div className="absolute top-0 right-0 bottom-0 w-1/3 bg-white/60 rounded-l-[3rem] hidden lg:block" />
        {/* Subtle blobs */}
        <div className="pointer-events-none absolute top-0 left-0 w-80 h-80 bg-brand-blue/4 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-1/4 w-64 h-64 bg-brand-red/3 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            {...fadeUp()}
            className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14"
          >
            <div className="max-w-xl">
              <span className="section-tag">What We Do Best</span>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold font-poppins text-gray-900 mt-2 leading-tight">
                Comprehensive Facilities<br />
                <span className="text-gradient-brand">&amp; Environmental Services</span>
              </h2>
            </div>
            <div className="lg:text-right max-w-xs">
              <p className="text-gray-500 text-sm font-inter leading-relaxed">
                Six integrated service lines delivering complete environmental management — all under one trusted roof.
              </p>
              <Link
                to="/services"
                className="inline-flex items-center gap-1.5 mt-4 text-brand-blue font-semibold text-sm hover:gap-2.5 transition-all font-inter cursor-pointer"
              >
                View all services <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-50px' }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
          >
            {services.map((s, i) => (
              <ServiceCard key={s.title} service={s} index={i} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          WHY CHOOSE US
      ══════════════════════════════════════════ */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Image column */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.85 }}
              className="relative flex items-center justify-center py-8"
            >
              {/* Background rings */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[85%] h-[90%] rounded-[2.5rem] border-2 border-brand-blue/8" />
              </div>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[95%] h-[100%] rounded-[2.5rem] bg-brand-blue/3" />
              </div>

              {/* Portrait card */}
              <div className="relative w-72 sm:w-80 mx-auto">
                <div className="relative rounded-[2rem] overflow-hidden shadow-2xl" style={{ aspectRatio: '3/4' }}>
                  <img
                    src="https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?auto=format&fit=crop&w=800&q=72&fm=webp"
                    alt="Lidav sanitary disposal services"
                    className="w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-brand-blue/25 via-transparent to-brand-blue/15" />
                </div>

                {/* Happy Clients badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.75, x: -16 }}
                  whileInView={{ opacity: 1, scale: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.55, duration: 0.5, ease: 'easeOut' }}
                  className="absolute -top-4 -left-2 sm:-top-5 sm:-left-6 rounded-2xl px-4 py-3 sm:px-5 sm:py-4"
                  style={{
                    background:           'rgba(2,174,242,0.95)',
                    backdropFilter:       'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    boxShadow:            '0 16px 48px rgba(2,174,242,0.30)',
                    border:               '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  <div className="text-[2rem] font-black font-poppins text-brand-red leading-none">
                    <Counter end={500} suffix="+" />
                  </div>
                  <p className="text-white/75 text-[11px] font-inter mt-1 tracking-wide">Happy Clients</p>
                </motion.div>

                {/* NEMA badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.75, x: 16 }}
                  whileInView={{ opacity: 1, scale: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.72, duration: 0.5, ease: 'easeOut' }}
                  className="absolute -bottom-4 -right-2 sm:-bottom-5 sm:-right-6 rounded-2xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3"
                  style={{
                    background:           'rgba(255,255,255,0.92)',
                    backdropFilter:       'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    boxShadow:            '0 8px 32px rgba(0,0,0,0.10)',
                    border:               '1px solid rgba(255,255,255,0.8)',
                  }}
                >
                  <div className="w-10 h-10 bg-brand-red rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                    <Award size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="font-bold font-poppins text-gray-900 text-sm leading-tight">NEMA Certified</p>
                    <p className="text-gray-400 text-[11px] font-inter mt-0.5">Licensed &amp; Compliant</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Text column */}
            <div>
              <motion.div {...fadeUp()}>
                <span className="section-tag">Why Choose Lidav</span>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-poppins text-gray-900 mt-2 mb-5 leading-tight">
                  Kenya's Most{' '}
                  <span className="text-gradient-brand">Trusted</span>{' '}
                  Facilities Partner
                </h2>
                <p className="text-gray-500 font-inter leading-relaxed mb-8">
                  With over 15 years of experience across Nairobi and Kenya, Lidav Enterprises combines professional expertise, cutting-edge equipment and eco-conscious practices to deliver results that exceed expectations.
                </p>
              </motion.div>

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="grid sm:grid-cols-2 gap-3"
              >
                {whyChoose.map(({ icon: Icon, text }) => (
                  <motion.div
                    key={text}
                    variants={staggerItem}
                    className="flex items-start gap-3 p-4 rounded-xl border transition-all duration-250 group cursor-default"
                    style={{
                      background:  'rgba(248,250,252,1)',
                      borderColor: 'rgba(226,232,240,0.8)',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background    = 'rgba(2,174,242,0.05)';
                      e.currentTarget.style.borderColor   = 'rgba(2,174,242,0.15)';
                      e.currentTarget.style.boxShadow     = '0 4px 16px rgba(2,174,242,0.08)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background    = 'rgba(248,250,252,1)';
                      e.currentTarget.style.borderColor   = 'rgba(226,232,240,0.8)';
                      e.currentTarget.style.boxShadow     = 'none';
                    }}
                  >
                    <div className="w-9 h-9 bg-brand-blue/8 group-hover:bg-brand-blue rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-250">
                      <Icon size={16} className="text-brand-blue group-hover:text-white transition-colors duration-250" />
                    </div>
                    <span className="text-gray-700 text-sm font-inter font-medium leading-snug mt-1">{text}</span>
                  </motion.div>
                ))}
              </motion.div>

              <motion.div {...fadeUp(0.3)} className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 bg-brand-blue text-gray-900 px-6 py-3 rounded-full text-sm font-bold hover:bg-brand-blue-dark transition-all hover:-translate-y-0.5 shadow-md cursor-pointer"
                >
                  Learn About Us <ArrowRight size={14} />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 border-2 border-brand-blue text-brand-blue px-6 py-3 rounded-full text-sm font-bold hover:bg-brand-blue hover:text-gray-900 transition-all hover:-translate-y-0.5 cursor-pointer"
                >
                  Get Free Quote
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          PROCESS — glassmorphism cards on brand-blue
      ══════════════════════════════════════════ */}
      <section className="py-20 bg-brand-blue relative overflow-hidden">
        {/* Background decoration */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle, #000 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="pointer-events-none absolute top-0 right-0 w-96 h-96 bg-brand-red/6 rounded-full blur-3xl animate-blob" style={{ animationDelay: '1s' }} />
        <div className="pointer-events-none absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-blob" style={{ animationDelay: '3s' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="inline-block text-brand-red font-bold tracking-widest uppercase text-xs mb-3">How We Work</span>
            <h2 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900">Our Simple 4-Step Process</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {[
              { step: '01', icon: Phone,        title: 'Contact Us',       desc: 'Call, email or fill our form. We respond within 1 hour.' },
              { step: '02', icon: Users,        title: 'Site Assessment',  desc: 'Our expert team visits to assess your specific needs.' },
              { step: '03', icon: Shield,       title: 'Service Delivery', desc: 'Trained professionals execute with precision and care.' },
              { step: '04', icon: CheckCircle2, title: 'Quality Check',    desc: 'We follow up to ensure 100% satisfaction every time.' },
            ].map(({ step, icon: Icon, title, desc }) => (
              <motion.div
                key={step}
                variants={staggerItem}
                className="relative rounded-2xl p-6 text-center transition-all duration-300 group cursor-default"
                style={{
                  background:           'rgba(255,255,255,0.10)',
                  backdropFilter:       'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border:               '1px solid rgba(255,255,255,0.16)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background  = 'rgba(255,255,255,0.18)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.24)';
                  e.currentTarget.style.transform   = 'translateY(-4px)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background  = 'rgba(255,255,255,0.10)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)';
                  e.currentTarget.style.transform   = 'translateY(0)';
                }}
              >
                <div className="text-5xl font-black font-poppins text-black/6 absolute top-4 right-5 select-none">{step}</div>
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-250 group-hover:bg-brand-red"
                  style={{ background: 'rgba(255,255,255,0.12)' }}
                >
                  <Icon size={22} className="text-gray-900" />
                </div>
                <h3 className="text-gray-900 font-bold font-poppins text-base mb-2">{title}</h3>
                <p className="text-gray-700 text-sm font-inter leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════ */}
      <section className="py-24 bg-[#f0f7ff] relative overflow-hidden">
        <div className="pointer-events-none absolute top-0 left-1/4 w-80 h-80 bg-brand-blue/6 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 right-1/4 w-64 h-64 bg-brand-red/4 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()} className="text-center mb-14">
            <span className="section-tag">Client Testimonials</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-poppins text-gray-900 mt-2">
              What Our Clients <span className="text-gradient-brand">Say</span>
            </h2>
            <div className="flex justify-center items-center gap-2 mt-5">
              <div className="w-12 h-1 bg-brand-blue rounded-full" />
              <div className="w-4 h-1 bg-brand-red rounded-full" />
              <div className="w-4 h-1 bg-brand-red rounded-full" />
            </div>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={testimonialIdx}
                initial={{ opacity: 0, x: 40, scale: 0.97 }}
                animate={{ opacity: 1, x: 0,  scale: 1    }}
                exit={{    opacity: 0, x: -40, scale: 0.97 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
                className="rounded-3xl p-8 sm:p-10 relative"
                style={{
                  background:           'rgba(255,255,255,0.85)',
                  backdropFilter:       'blur(16px)',
                  WebkitBackdropFilter: 'blur(16px)',
                  border:               '1px solid rgba(255,255,255,0.70)',
                  boxShadow:            '0 20px 60px rgba(2,174,242,0.10)',
                }}
              >
                {/* Decorative quote */}
                <div className="text-8xl text-brand-blue/6 font-serif absolute top-2 left-7 leading-none select-none pointer-events-none">"</div>

                <div className="flex mb-5 gap-1">
                  {Array.from({ length: testimonials[testimonialIdx].rating }).map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 text-base sm:text-lg font-inter leading-relaxed mb-8 relative z-10 font-medium">
                  "{testimonials[testimonialIdx].text}"
                </p>
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black font-poppins text-lg shadow-md"
                    style={{ background: 'linear-gradient(135deg, #02AEF2, #018bc2)' }}
                  >
                    {testimonials[testimonialIdx].name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold font-poppins text-gray-900">{testimonials[testimonialIdx].name}</p>
                    <p className="text-gray-500 text-sm font-inter mt-0.5">{testimonials[testimonialIdx].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Controls */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setTestimonialIdx(v => (v - 1 + testimonials.length) % testimonials.length)}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-brand-blue hover:text-white transition-all duration-250 text-gray-600 cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
              >
                <ChevronLeft size={18} />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTestimonialIdx(i)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${i === testimonialIdx ? 'w-6 h-2.5 bg-brand-red' : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setTestimonialIdx(v => (v + 1) % testimonials.length)}
                className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-brand-blue hover:text-white transition-all duration-250 text-gray-600 cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          CTA BANNER — animated gradient + glass card
      ══════════════════════════════════════════ */}
      <section className="relative py-24 overflow-hidden bg-gradient-animated">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-20 -right-20 w-96 h-96 bg-brand-red/12 rounded-full blur-3xl animate-blob" style={{ animationDelay: '0.5s' }} />
        <div className="pointer-events-none absolute -bottom-20 -left-20 w-96 h-96 bg-black/8 rounded-full blur-3xl animate-blob" style={{ animationDelay: '3s' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div
            {...fadeUp()}
            className="rounded-3xl p-10 sm:p-14 text-center"
            style={{
              background:           'rgba(0,0,0,0.08)',
              backdropFilter:       'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              border:               '1px solid rgba(255,255,255,0.14)',
            }}
          >
            <span className="inline-block text-brand-red font-bold tracking-widest uppercase text-xs mb-4">Ready to Get Started?</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-poppins text-gray-900 mb-5 leading-tight">
              Protect Your Space Today.<br />
              <span className="text-brand-red">Call Us Right Now.</span>
            </h2>
            <p className="text-gray-800 font-inter text-base sm:text-lg mb-8 max-w-xl mx-auto font-medium">
              Get a free site assessment and customised quote within 24 hours. Our experts are standing by.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="tel:0722148999"
                className="group flex items-center gap-3 bg-brand-red hover:bg-brand-red-dark text-white px-8 py-4 rounded-full font-bold font-poppins text-lg transition-all duration-200 shadow-xl hover:-translate-y-1 hover:shadow-2xl cursor-pointer"
                style={{ boxShadow: '0 8px 32px rgba(227,6,19,0.30)' }}
              >
                <Phone size={20} className="group-hover:animate-pulse" />
                0722 148 999
              </a>
              <Link
                to="/contact"
                className="flex items-center gap-2 border-2 border-black/12 text-gray-900 px-7 py-4 rounded-full font-semibold font-poppins transition-all duration-200 hover:-translate-y-1 cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
              >
                Get a Free Quote <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
